import { useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'

import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import ExperienceSearchTileList from 'components/Experiences/ExperienceLists/ExperienceSearchTileList'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import { fetchPlaceByGeoCoords } from 'actions/PlaceActions'
import { ANYWHERE_PLACE_ID } from 'constants/search'

const CarouselList = styled.div`
  padding: ${rem(40)} 0;

  > * + * {
    margin-top: ${rem(32)};
  }
`

const PLACES_TYPE: Array<App.PlaceType> = ['city', 'province_state', 'country']

function ExperienceOffersLoader(props: App.HubPageComponentExperienceOffersLoader) {
  const dispatch = useAppDispatch()

  const coordsToPlace = useAppSelector((state) => state.destination.coordsToPlace)

  const { title, location, categories, offersToShow, filters, placeType } = props

  useEffect(() => {
    if (location && !placeType) {
      dispatch(fetchPlaceByGeoCoords(location.lat, location.lon, { levels: PLACES_TYPE }))
    } else if (location && placeType) {
      dispatch(fetchPlaceByGeoCoords(location.lat, location.lon, { level: placeType }))
    }
  }, [dispatch, location, placeType])

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  const place = useMemo(() => {
    const key = `${location?.lat}-${location?.lon}`
    return coordsToPlace[key]
  }, [coordsToPlace, location])

  const categoryCodes = useMemo(
    // Contentful does not provide a field with multiple labels and values (dropdown) to the users,
    // so the categories are multiple checkbox fields with the concatenation of the name of the category + code
    // e.g. Activities code=1001
    () => categories?.map((category) => parseInt(category.replace(/^.*code=/, '').trim())),
    [categories],
  )

  const campaigns = filters?.campaigns

  if (place) {
    return (
      <>
        <Heading variant="heading3" align="center">
          {title}
        </Heading>
        <LayoutContainer>
          <CarouselList>
            <ExperienceSearchTileList
              placeId={place.id}
              categories={categoryCodes}
              limit={offersToShow}
              campaigns={campaigns}
            />
          </CarouselList>
        </LayoutContainer>
      </>
    )
  }

  // fallback when has no location
  if (!location && (categoryCodes || campaigns)) {
    return (
      <>
        <Heading variant="heading3" align="center">
          {title}
        </Heading>
        <LayoutContainer>
          <CarouselList>
            <ExperienceSearchTileList
              placeId={ANYWHERE_PLACE_ID}
              categories={categoryCodes}
              limit={offersToShow}
              campaigns={campaigns}
            />
          </CarouselList>
        </LayoutContainer>
      </>
    )
  }

  return null
}

export default ExperienceOffersLoader
